// The one bar of full screen for a doc, sheet, deck or files board (Features 7): Back, the board's name (the same one
// the board view has, so renaming it and moving to another board work as they do there), where you are inside the
// board, then the file's own tools as buttons with words, hiding the browser too, and the widgets the board holds.
// Nothing floats over the work: the board's surface fills what is under it (chrome.css, .is-fs-tab).

import { useEffect, useRef, useState } from "react";
import { ArrowLeft, LayoutGrid, Maximize2 } from "lucide-react";
import { kindOf } from "../../shared/boardKinds";
import { shelfState } from "../../shared/shelf";
import { useShelfItems } from "../boards/pipeline/Shelf";
import { closeChat } from "../agent/store";
import { currentBoard, useStore } from "../state/store";
import { publicSlugHere } from "../state/publicLink";
import { BoardMenu, BoardName } from "./TopBar";
import { TabEnd, TabStrip } from "./BoardTabs";
import { useTabLook, useTabsOn } from "./useTabs";
import { browserFullscreen, exitFullscreen, useFs } from "./Fullscreen";
import { cx } from "../lib/util";

/** The kinds that keep their widgets in a shelf down the right, which full screen slides in from the bar. */
const SHELVED = new Set(["sheet"]);

export function FullscreenBar() {
  const board = useStore(currentBoard);
  const guest = useStore((s) => !!s.guest);
  const [menu, setMenu] = useState(false);
  const shelf = useFs((s) => s.shelf);
  const widgets = useShelfItems(board?.id ?? "");
  const tabs = useTabsOn();
  const icons = useTabLook().icons;
  const where = useRef<HTMLSpanElement>(null);
  const tools = useRef<HTMLDivElement>(null);
  useEffect(() => {
    useFs.setState({ where: where.current, tools: tools.current });
    return () => useFs.setState({ where: null, tools: null });
  }, [board?.id]);
  if (!board) return null;
  // a coworker's, a public link's or an archived board is only looked at: its name is not a switcher, and the file's editing tools are absent
  const fixed = guest || !!board.archived || !!publicSlugHere();
  const hasShelf = SHELVED.has(kindOf(board)) && shelfState(board, widgets) !== "none";
  return (
    <div className="fs-bar" role="toolbar" aria-label="Full screen">
      <button className="fs-btn fs-back is-word" onClick={() => exitFullscreen()} aria-keyshortcuts="Escape">
        <ArrowLeft size={15} />
        <span>Back</span>
      </button>
      {/* with the boards as tabs, the open tab is the board's name: it is not drawn a second time */}
      {tabs && !fixed ? (
        <span className={cx("fs-tabs", !icons && "no-icons")}>
          <TabStrip board={board} />
          <TabEnd />
        </span>
      ) : (
        <span className="fs-name">
          {fixed ? (
            <span className="board-pill-name is-fixed">
              {board.emoji && <span className="board-emoji">{board.emoji}</span>}
              <span>{board.name}</span>
            </span>
          ) : (
            <BoardName board={board} menu={menu} setMenu={setMenu} />
          )}
          {menu && !fixed && <BoardMenu onClose={() => setMenu(false)} />}
        </span>
      )}
      <span className="fs-where" ref={where} />
      <div className="fs-tools" ref={tools} />
      {hasShelf && (
        <button
          className={cx("fs-btn", shelf && "is-on")}
          aria-pressed={shelf}
          aria-label={`${shelf ? "Hide" : "Show"} the widgets (${widgets.length})`}
          data-tip={`${shelf ? "Hide" : "Show"} the widgets`}
          onClick={() => {
            if (!shelf) closeChat();
            useFs.setState({ shelf: !shelf });
          }}
        >
          <LayoutGrid size={15} />
          <span>Widgets</span>
          <span className="fs-count">{widgets.length}</span>
        </button>
      )}
      {/* where the browser cannot go full screen, the button is not here */}
      {document.fullscreenEnabled && (
        <button className="fs-btn is-word" onClick={() => void browserFullscreen()} data-tip="Hide the browser's tabs too. Esc comes back here">
          <Maximize2 size={15} />
          <span>Hide browser</span>
        </button>
      )}
    </div>
  );
}
